import { useEffect, useState } from "react";
import { X, Loader2, Clock, User } from "lucide-react";
import { GetDealHistory } from "../Services/DealService";
import { formatDateTimeU } from "../../../../Utils/FormUtils";

const DealHistoryDrawer = ({ open, onClose, deal }) => {
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState([]);
  
  const formatValue = (value) => {
  if (!value) return "-";

  const isDate =
    typeof value === "string" &&
    !isNaN(new Date(value).getTime()) &&
    value.includes("-");

  if (isDate) return formatDateTimeU(value); // ← use utility, it auto-detects DateOnly vs DateTime

  return value;
};

  const buildTimeline = (history) => {
    return history.map((item, index) => {
      const prev = history[index - 1];
      const changes = [];

      if (prev) {
        const track = (label, key, formatter) => {
          if (item[key] !== prev[key]) {
            changes.push({
              label,
              from: prev[key],
              to: item[key],
              format: formatter,
            });
          }
        };

        track("Status", "dealStatus");
        track("Interview Date", "interviewDate", formatDateTimeU);
        track("Offered CTC", "offeredCTC");
        track("Expected Joining", "expectedJoiningDate", formatDateTimeU);
        track("Joining Date", "joiningDate", formatDateTimeU);
        track("Owner", "ownerName");
      }

      return {
        id: item.historyID,
        status: item.dealStatus,
        actionType: item.actionType,
        remark: item.remark,
        actionBy: item.actionBy,
        actionDate: item.actionDate,
        changes,
      };
    });
  };

  const fetchHistory = async () => {
    if (!deal?.dealUID) return;

    try {
      setLoading(true);
      const res = await GetDealHistory(deal.dealUID);

      const sorted = (res?.history || []).sort(
        (a, b) => a.historyID - b.historyID,
      );

      setTimeline(buildTimeline(sorted));
    } catch (err) {
      console.error(err);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchHistory();
  }, [open, deal]);

  return (
    <div className={`fixed inset-0 z-50 ${open ? "visible" : "invisible"}`}>
      {/* overlay */}
      <div
        onClick={onClose}
        className={`absolute inset-0 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        } bg-black/50 backdrop-blur-sm`}
      />

      {/* DRAWER (kept slightly bigger) */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-[720px] bg-white shadow-2xl border-l transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50">
          <div>
            <h2 className="font-bold text-slate-800">Deal History</h2>
            <p className="text-xs text-slate-500">
              {deal?.name} • {deal?.dealUID}
            </p>
          </div>

          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg hover:bg-slate-200 flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 overflow-y-auto h-full pb-24">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="animate-spin text-slate-500" />
            </div>
          ) : timeline.length === 0 ? (
            <p className="text-sm text-slate-500 text-center mt-10">
              No history found
            </p>
          ) : (
            <div className="space-y-5">
              {timeline.map((t, idx) => (
                <div key={t.id} className="flex gap-4">
                  {/* TIMELINE */}
                  <div className="flex flex-col items-center">
                    {/* Dot */}
                    <div className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-md mt-1" />
                    {/* Line connecting to next (except last) */}
                    {idx < timeline.length - 1 && (
                      <div className="w-0.5 flex-1 bg-gradient-to-b from-blue-300 to-blue-100" style={{ minHeight: "120px" }} />
                    )}
                  </div>

                  {/* CARD */}
                  <div className="flex-1 border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                    {/* HEADER */}
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-sm text-slate-800">
                          {t.status}
                        </p>
                        {t.actionType === "CREATED" && (
                          <div className="mt-2 text-xs text-slate-600 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400">Role:</span>
                              <span className="font-medium text-slate-700">
                                {deal?.targetRole || "-"}
                              </span>
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="text-slate-400">Company:</span>
                              <span className="font-medium text-slate-700">
                                {deal?.targetOrganization || "-"}
                              </span>
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                          <Clock size={12} />
                          {formatDateTimeU(t.actionDate)}
                        </p>
                      </div>

                      <span className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-600">
                        {t.actionType}
                      </span>
                    </div>

                    {/* REMARK (CRM evidence style) */}
                    {/* REMARK (conversation / recorded statement style) */}
                    {t.remark && (
                      <div className="mt-3">
                        <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-100">
                          <p className="text-[13px] text-slate-700 leading-relaxed italic">
                            <span className="text-amber-600 font-semibold mr-1">
                              “
                            </span>
                            {t.remark}
                            <span className="text-amber-600 font-semibold ml-1">
                              ”
                            </span>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* CHANGES */}
                    {t.changes.length > 0 && (
                      <div className="mt-3 text-xs text-slate-600 space-y-1">
                        {t.changes.map((c, i) => (
                          <div key={i} className="flex gap-2">
                            <span className="font-medium text-slate-700">
                              {c.label}:
                            </span>

                            <span className="text-red-500">
                              {formatValue(c.from)}
                            </span>

                            <span>→</span>

                            <span className="text-green-600">
                              {formatValue(c.to)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* FOOTER */}
                    <div className="flex items-center gap-1 mt-3 text-[11px] text-slate-500">
                      <User size={12} />
                      {t.actionBy || "System"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealHistoryDrawer;
